import { jsx as _jsx } from "react/jsx-runtime";
import { noop } from "@mantine/core";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { debounce } from "es-toolkit";
export const StyleEditorContext = createContext({});
export const useStyleEditor = () => useContext(StyleEditorContext);
export const useCaptureEvent = () => {
    const { captureEvent } = useStyleEditor();
    return captureEvent;
};
export const useDebouncedCaptureEvent = () => {
    const { captureEvent } = useStyleEditor();
    return useMemo(() => debounce(captureEvent, 400), [captureEvent]);
};
export const useMap = () => {
    const { map } = useStyleEditor();
    return map;
};
export const useFeatureFlags = () => {
    const { featureFlags } = useStyleEditor();
    return featureFlags;
};
const defaultFeatureFlags = {};
export const StyleEditorProvider = ({ mode = "edit", children, mapkaAccountName, featureFlags = defaultFeatureFlags, captureEvent = noop, onChatChange, }) => {
    const [map, setMap] = useState(undefined);
    const [secondaryMap, setSecondaryMap] = useState(undefined);
    const eventHandlersRef = useRef(new Map());
    const on = useCallback(((event, handler) => {
        const handlers = eventHandlersRef.current.getOrInsert(event, new Set());
        handlers.add(handler);
    }), []);
    const off = useCallback(((event, handler) => {
        eventHandlersRef.current.get(event)?.delete(handler);
    }), []);
    const emit = useCallback((event, payload) => {
        const handlers = eventHandlersRef.current.get(event);
        if (handlers) {
            for (const handler of handlers) {
                handler(payload);
            }
        }
    }, []);
    const value = useMemo(() => {
        const disabled = mode === "inspect";
        const context = {
            mapkaAccountName,
            map,
            mode,
            disabled,
            featureFlags,
            secondaryMap,
            setMap,
            setSecondaryMap,
            captureEvent,
            onChatChange,
            on,
            off,
            emit,
        };
        return context;
    }, [
        mapkaAccountName,
        map,
        secondaryMap,
        featureFlags,
        setMap,
        setSecondaryMap,
        captureEvent,
        onChatChange,
        on,
        off,
        emit,
    ]);
    return _jsx(StyleEditorContext.Provider, { value: value, children: children });
};
